import {
  Box,
  Button,
  ButtonBase,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditIcon from "@mui/icons-material/Edit";
import HubIcon from "@mui/icons-material/Hub";
import PsychologyIcon from "@mui/icons-material/Psychology";
import StorageIcon from "@mui/icons-material/Storage";
import React from "react";
import { useNavigate } from "react-router-dom";

const foundationCards = [
  { title: ["Deep learning", ""], Icon: PsychologyIcon },
  { title: ["Annotated datasets", ""], Icon: StorageIcon },
  { title: ["Annotation tool", ""], Icon: EditIcon },
  { title: ["Synthetic data", ""], Icon: AutoFixHighIcon },
  {
    title: ["Training and inference", ""],
    Icon: HubIcon,
  },
];

const applicationCards = [
  { title: "Pose estimation", image: "pose_estimation.png" },
  { title: "Defect detection", image: "defect_detection.png" },
  { title: "Object detection", image: "object_detecton.png" },
  { title: "Object counting", image: "object_counting.png" },
];

const interactiveCardSx = {
  cursor: "pointer",
  display: "block",
  textAlign: "left",
  width: "100%",
  transition: "transform 160ms ease, box-shadow 160ms ease",
  "&:hover, &:focus-visible": {
    boxShadow: "0 8px 16px rgba(31, 45, 61, 0.18)",
    transform: "translateY(-4px)",
  },
  "&:focus-visible": {
    outline: "3px solid #00bdaf",
    outlineOffset: 3,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
};

const FoundationCard = ({ title, Icon }) => (
  <Box
    sx={{
      position: "relative",
      zIndex: 1,
      "&::after": {
        bgcolor: "#cbd5df",
        content: '""',
        display: { xs: "none", md: "block" },
        height: 28,
        left: "50%",
        position: "absolute",
        bottom: -28,
        transform: "translateX(-50%)",
        width: 3,
        zIndex: 0,
      },
    }}
  >
    <Card
      elevation={0}
      sx={{
        border: 2,
        borderColor: "rgb(243, 243, 243)",
        borderRadius: 6,
        p: 2,
        position: "relative",
        zIndex: 1,
        backgroundColor: "#2c3e50",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          justifyContent: "center",
          minHeight: 80,
        }}
      >
        <Icon aria-hidden="true" sx={{ color: "#ffffff", fontSize: 28 }} />
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="#ffffff"
          sx={{
            textTransform: "uppercase",
            lineHeight: 1.3,
            textAlign: "center",
          }}
        >
          {title.map((line) => (
            <Box component="span" key={line} sx={{ display: "block" }}>
              {line}
            </Box>
          ))}
        </Typography>
      </Box>
    </Card>
  </Box>
);

const ApplicationCard = ({ title, image, onClick }) => (
  <ButtonBase
    aria-label={title}
    onClick={onClick}
    type="button"
    sx={{
      ...interactiveCardSx,
      position: "relative",
      zIndex: 1,
      "&::before": {
        bgcolor: "#cbd5df",
        content: '""',
        display: { xs: "none", md: "block" },
        height: 24,
        left: "50%",
        position: "absolute",
        top: -24,
        transform: "translateX(-50%)",
        width: 3,
        zIndex: 0,
      },
    }}
  >
    <Card
      elevation={0}
      sx={{
        border: 2,
        borderColor: "rgb(243, 243, 243)",
        borderRadius: 6,
        p: 1.5,
        position: "relative",
        zIndex: 1,
        backgroundColor: "#ecf1f5",
      }}
    >
      <Box
        component="img"
        alt=""
        src={`${process.env.PUBLIC_URL}/LandingPage/${image}`}
        sx={{
          aspectRatio: "9/7",
          borderRadius: 2,
          display: "block",
          objectFit: "cover",
          width: "100%",
        }}
      />
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ mt: 1.5, textAlign: "center", textTransform: "uppercase" }}
      >
        {title}
      </Typography>
    </Card>
  </ButtonBase>
);

const Landing = () => {
  const navigate = useNavigate();
  const [edgeDialogOpen, setEdgeDialogOpen] = React.useState(false);

  return (
    <Box
      sx={{
        mx: "auto",
        mt: 1,
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
        width: { xs: "85%", sm: "85%" },
        display: "flex",
        flexDirection: "column", // ensure vertical stacking
        p: 1,
        // backgroundColor: "#f5f5f5",
      }}
    >
      <Typography
        variant="h3"
        fontWeight={700}
        color="black"
        mt={4}
        sx={{ textAlign: "center" }}
      >
        Welcome to EARN!
      </Typography>
      <Typography
        variant="h6"
        color="rgb(158, 151, 151)"
        mt={2}
        sx={{ textAlign: "center" }}
      >
        Discover how AI can be turned into powerful solutions for image
        analysis.
      </Typography>
      <Box sx={{ mt: 5, overflowX: "auto", pb: 4 }}>
        <Box
          aria-label="Workspace cards"
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "160px repeat(5, minmax(150px, 1fr)) 160px",
              md: "160px repeat(5, minmax(0, 1fr)) 160px",
            },
            minWidth: { xs: 1166, md: 0 },
            position: "relative",
            "&::before": {
              bgcolor: "#cbd5df",
              content: '""',
              display: { xs: "none", md: "block" },
              height: 3,
              left: "calc(10% + 134px)",
              position: "absolute",
              right: "calc(10% + 134px)",
              top: "calc(100% + 28px)",
              zIndex: 0,
            },
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: "#2c3e50",
              alignSelf: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            Foundation stack
          </Typography>
          {foundationCards.map((card) => (
            <FoundationCard
              key={card.title.join(" ")}
              title={card.title}
              Icon={card.Icon}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "120px minmax(0, 1fr)",
            md: "150px minmax(0, 1fr) 150px",
          },
          mt: 4,
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            color: "#2c3e50",
            alignSelf: "center",
            gridRow: { xs: "1 / span 2", md: "auto" },
          }}
        >
          Core engine
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridColumn: 2,
            position: "relative",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)",
            },
            "&::before": {
              bgcolor: "#cbd5df",
              content: '""',
              display: { xs: "none", md: "block" },
              height: 27,
              left: "50%",
              position: "absolute",
              top: -36,
              transform: "translateX(-50%)",
              width: 3,
            },
            "&::after": {
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "9px solid #cbd5df",
              content: '""',
              display: { xs: "none", md: "block" },
              left: "50%",
              position: "absolute",
              top: -9,
              transform: "translateX(-50%)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              gridColumn: { xs: 1, md: 2 },
              justifySelf: { md: "center" },
              minWidth: 0,
              width: { xs: "100%", md: "70%" },
              "&::before": {
                bgcolor: "#cbd5df",
                content: '""',
                display: { xs: "none", md: "block" },
                height: 3,
                position: "absolute",
                right: { md: "-26.5%" },
                top: "50%",
                width: { md: "26.5%" },
              },
              "&::after": {
                bgcolor: "#cbd5df",
                content: '""',
                display: { xs: "none", md: "block" },
                height: 24,
                left: "50%",
                position: "absolute",
                bottom: -24,
                transform: "translateX(-50%)",
                width: 3,
              },
            }}
          >
            <ButtonBase
              aria-label="AI for image analysis in cloud infrastructure"
              onClick={() => navigate("/model-zoo")}
              type="button"
              sx={{ ...interactiveCardSx, borderRadius: 6 }}
            >
              <Card
                elevation={0}
                sx={{
                  border: 2,
                  borderColor: "rgb(243, 243, 243)",
                  borderRadius: 6,
                  p: 2,
                  backgroundColor: "#00bdaf",
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    minHeight: 160,
                  }}
                >
                  <Typography
                    component="h2"
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: "#ffffff",
                      textAlign: "center",
                      textTransform: "uppercase",
                    }}
                  >
                    EARN Cloud Platform
                  </Typography>
                </Box>
              </Card>
            </ButtonBase>
            <Box
              aria-hidden="true"
              sx={{
                borderBottom: "6px solid transparent",
                borderLeft: "9px solid #cbd5df",
                borderTop: "6px solid transparent",
                display: { xs: "none", md: "block" },
                height: 0,
                position: "absolute",
                right: { md: "-26.5%" },
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
              }}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                bgcolor: "background.paper",
                color: "#2c3e50",
                display: { xs: "none", md: "block" },
                left: "calc(100% + 14.4%)",
                position: "absolute",
                top: "calc(50% - 15px)",
                transform: "translate(-50%, -50%)",
                whiteSpace: "nowrap",
                zIndex: 2,
              }}
            >
              Deployment
            </Typography>
          </Box>
          <ButtonBase
            aria-label="Edge computing information"
            aria-haspopup="dialog"
            onClick={() => setEdgeDialogOpen(true)}
            type="button"
            sx={{
              ...interactiveCardSx,
              borderRadius: 6,
              gridColumn: { xs: 1, md: 3 },
              justifySelf: "start",
              alignSelf: "center",
              width: { xs: "85%", md: "90%" },
              minWidth: 0,
            }}
          >
            <Card
              elevation={0}
              sx={{
                border: 2,
                borderColor: "rgb(243, 243, 243)",
                borderRadius: 6,
                p: 1.5,
                backgroundColor: "#00978c",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  minHeight: 80,
                }}
              >
                <Typography
                  component="h2"
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    color: "#ffffff",
                    textAlign: "center",
                    textTransform: "uppercase",
                    width: "100%",
                  }}
                >
                  Edge computing
                </Typography>
              </Box>
            </Card>
          </ButtonBase>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "120px minmax(0, 1fr)",
            md: "150px minmax(0, 1fr) 150px",
          },
          mt: 6,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            color: "#2c3e50",
            alignSelf: "center",
            gridRow: { xs: "1 / span 2", md: "auto" },
          }}
        >
          Applications
        </Typography>
        <Box
          aria-label="Application cards"
          sx={{
            display: "grid",
            gap: 2,
            gridColumn: 2,
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            position: "relative",
            "&::before": {
              bgcolor: "#cbd5df",
              content: '""',
              display: { xs: "none", md: "block" },
              height: 3,
              left: "calc(12.5% - 6px)",
              position: "absolute",
              right: "calc(12.5% - 6px)",
              top: -24,
            },
          }}
        >
          {applicationCards.map(({ title, image }) => (
            <ApplicationCard
              key={title}
              title={title}
              image={image}
              onClick={() =>
                navigate(`/model-zoo?application=${encodeURIComponent(title)}`)
              }
            />
          ))}
        </Box>
      </Box>
      <Dialog
        aria-labelledby="edge-dialog-title"
        maxWidth="xs"
        onClose={() => setEdgeDialogOpen(false)}
        open={edgeDialogOpen}
      >
        <DialogTitle id="edge-dialog-title">Edge computing</DialogTitle>
        <DialogContent>
          <Typography>
            For more information on deployment of AI models, contact{" "}
            <Link href="mailto:abdellatif.bey-temsamani@flandersmake.be">
              abdellatif.bey-temsamani@flandersmake.be
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdgeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Landing;
